// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation) and pauses on hover. One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal: this.#ac.signal });

    this.addEventListener("mouseenter", () => this.#animation?.pause(), { signal: this.#ac.signal });
    this.addEventListener("mouseleave", () => this.#animation?.play(), { signal: this.#ac.signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#animation?.cancel();
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track || this.#mql.matches) return;

    // Ensure exactly two copies of the content so the loop is seamless.
    if (!track.dataset.cloned) {
      track.append(...[...track.children].map((n) => n.cloneNode(true)));
      track.dataset.cloned = "true";
    }

    const distance = track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
